import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState, type ReactElement } from 'react'
import { z } from 'zod'

import { DataTablePagination } from '~/components/data/DataTablePagination'
import {
  UnverifiedQueue,
  UserDetailsSheet,
  usersKeys,
  type UserRow,
} from '~/features/users'
import { http } from '~/lib/http'
import {
  toPaginatedResult,
  usePaginatedQuery,
  type PaginatedParams,
  type PaginatedResult,
} from '~/lib/query/use-paginated-query'
import { useUrlState } from '~/lib/query/use-url-state'

/**
 * `/admin/users/unverified` — cleanup queue for unverified accounts.
 *
 * URL search keys: `page`, `limit`.
 *
 * Fetches users with `status=unverified` from `/api/v1/admin/users`.
 * The page is URL-paginated so admins can paginate through the queue
 * without losing their place when an action refetches the list.
 */
const unverifiedSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(10).max(100).default(50),
})
type UnverifiedSearch = z.infer<typeof unverifiedSearchSchema>

const URL_DEFAULTS: UnverifiedSearch = { page: 1, limit: 50 }

export const Route = createFileRoute('/admin/users/unverified')({
  validateSearch: unverifiedSearchSchema,
  component: UnverifiedPage,
})

interface UsersListPayload {
  readonly users?: readonly UserRow[]
  readonly items?: readonly UserRow[]
  readonly total?: number
  readonly pagination?: {
    readonly page?: number
    readonly limit?: number
    readonly total?: number
    readonly totalPages?: number
  }
}

async function fetchUnverifiedPage(
  params: PaginatedParams,
): Promise<PaginatedResult<UserRow>> {
  // Over-fetch so the client-side `emailVerified === false` slice still
  // fills the page (mirrors users-list logic in features/users/queries).
  const query: Record<string, string | number | undefined> = {
    page: params.page,
    limit: params.limit * 4,
    offset: (params.page - 1) * params.limit,
  }
  const raw = await http.get<UsersListPayload>('/api/v1/admin/users', {
    query,
  })
  const rows = (raw.users ?? raw.items ?? []) as readonly UserRow[]
  const unverified = rows.filter((u) => u.emailVerified === false)
  const total =
    raw.pagination?.total ?? raw.total ?? unverified.length
  return toPaginatedResult(unverified, total, params, raw.pagination)
}

function UnverifiedPage(): ReactElement {
  const search = Route.useSearch()
  const [, setUrl] = useUrlState(unverifiedSearchSchema, URL_DEFAULTS)
  const [openUserId, setOpenUserId] = useState<string | null>(null)

  const params: PaginatedParams = {
    page: search.page,
    limit: search.limit,
  }
  const usersQuery = usePaginatedQuery<UserRow>({
    queryKey: [...usersKeys.all, 'unverified-paginated'],
    params,
    fetcher: fetchUnverifiedPage,
  })

  const users = usersQuery.data?.data ?? []
  const pagination = usersQuery.data?.pagination

  return (
    <div className="ka-page">
      <div className="ka-page-section">
        <Link
          to="/admin/users"
          className="ka-btn ka-btn--ghost ka-btn--sm"
        >
          <ArrowLeft size={12} aria-hidden="true" /> User Management
        </Link>
      </div>

      {usersQuery.isError ? (
        <div className="ka-card" role="alert">
          <div className="ka-empty">
            <div className="ka-empty-title">
              Couldn't load the unverified queue
            </div>
            <div className="ka-empty-body">
              {isForbidden(usersQuery.error)
                ? 'Restricted — request access from a super_admin.'
                : usersQuery.error instanceof Error
                  ? usersQuery.error.message
                  : 'Unknown error'}
            </div>
          </div>
        </div>
      ) : (
        <>
          <UnverifiedQueue
            users={users}
            isLoading={usersQuery.isPending}
            onOpenUser={setOpenUserId}
            onForceDelete={async (ids) => {
              await http.post(
                '/api/v1/admin/users/force-delete-unverified',
                { user_ids: ids },
                { idempotencyKey: crypto.randomUUID() },
              )
              await usersQuery.refetch()
            }}
          />
          <DataTablePagination
            page={pagination?.page ?? search.page}
            limit={pagination?.limit ?? search.limit}
            total={pagination?.total ?? users.length}
            isLoading={usersQuery.isPending}
            onChange={({ page, limit }) => setUrl({ page, limit })}
          />
        </>
      )}

      <UserDetailsSheet
        userId={openUserId ?? ''}
        open={openUserId !== null}
        onClose={() => setOpenUserId(null)}
      />
    </div>
  )
}

function isForbidden(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const candidate = error as { readonly code?: unknown }
  return candidate.code === 'forbidden'
}
